import { FC, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  addNotification,
  removeNotification,
} from 'src/shared/state/notifications-slice';
import { SnackbarTypes } from '@components/notifications/enums';
import { SNACKBAR_LOADING_ID_SUFFIX } from '@components/notifications/constants';
import useIntl from '@i18n/useIntl';
import {
  COMBINE_ACCOUNTS_CONFIRM_ERROR_KEY,
  COMBINE_ACCOUNTS_IN_PROGRESS_SNACKBAR_KEY,
} from '@features/combine-accounts/constants';
// Imported from the review page's directory, matching how the upgrade notifier reaches into the
// choose page for its own hook.
import useCombineProgress from '../../combineReview/useCombineProgress';

/**
 * What the review page hands over when a combine outlives the modal's window.
 *
 * Exported so the page doing the handing over is typed against the same shape rather than composing
 * router state by hand.
 */
export interface CombineNavigationState {
  /** The account being combined into, which is what the status read is keyed on. */
  combiningAccountId?: string;
  /**
   * The combined account's regional entity, for the campaign manager link.
   *
   * Carried across the navigation because only the review page has it, from review data fetched
   * before the combine. The status read does not return it.
   */
  entityId?: string;
}

/** Distinct from the review page's own error id so one cannot dismiss the other. */
const PROGRESS_NOTIFICATION_ID = 'combine-accounts-progress';

/**
 * Reports a combine that outlived the confirmation modal, and redirects once it finishes.
 *
 * <h3>Why this lives here rather than on the review page</h3>
 *
 * A combine that takes longer than the modal's window stops holding the user: the design closes the
 * modal, returns them to this page, and reports progress in a snackbar while the work continues.
 * That is a navigation, so the page that started the combine is unmounted and cannot be the one that
 * sees it finish. The account arrives in router state and the watching resumes here.
 *
 * <h3>Why it does not survive a reload</h3>
 *
 * The hand-over rides on router state, which a reload discards, so a reload loses the report while
 * the combine itself carries on server-side. Deliberate: this reports a transient operation, and
 * persisting it would need a rule for when a stale entry expires.
 *
 * Renders nothing. The snackbars are the app-level ones, which are already positioned and already
 * mounted, so this only dispatches.
 */
const CombineProgressNotifier: FC = () => {
  const { intl } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { followCombine } = useCombineProgress();

  /*
    The hand-over is a one-time event, captured on the first render so the watch below can run once
    off empty dependencies.

    Reading it reactively is a trap the upgrade lane already fell into: the effect clears the router
    state once consumed, which changes location.state, which re-runs an effect depending on it.
    React tears down before re-running, so the clearing aborts the watch it had just started. A ref
    initialiser runs once, so this holds the state as it arrived even though the render after the
    clearing sees null.
  */
  const handoverRef = useRef<CombineNavigationState | null>(
    (location.state ?? null) as CombineNavigationState | null,
  );
  // Held in refs for the same reason: naming them as dependencies would make the watch restartable,
  // and a second watch on one hand-over doubles the requests and races two reports.
  const pathnameRef = useRef(location.pathname);
  const followRef = useRef(followCombine);
  followRef.current = followCombine;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const intlRef = useRef(intl);
  intlRef.current = intl;

  useEffect(() => {
    const globalAccountId = handoverRef.current?.combiningAccountId;
    const entityId = handoverRef.current?.entityId;

    if (!globalAccountId) {
      // An ordinary visit to this page, with no combine to report.
      return undefined;
    }

    /*
      Consumed, so the hand-over is cleared from the history entry. Left in place it would be read
      again on a later back-navigation and restart a watch on a combine that finished long ago.
      Everything needed was captured above.
    */
    navigateRef.current(pathnameRef.current, { replace: true, state: null });

    /*
      The loading variant carries a default duration of zero, so it stays until it is removed rather
      than timing out under a combine that is still running.
    */
    dispatchRef.current(
      addNotification({
        id: PROGRESS_NOTIFICATION_ID,
        type: SnackbarTypes.Loading,
        message: intlRef.current.getMessage(
          COMBINE_ACCOUNTS_IN_PROGRESS_SNACKBAR_KEY,
        ),
      }),
    );

    const controller = new AbortController();
    let abandoned = false;

    followRef
      .current(globalAccountId, controller.signal)
      .then((progress) => {
        if (abandoned) {
          return;
        }
        /*
          The slice rewrites the id on the way in, appending a per-type suffix, so the stored id is
          not the one dispatched and removal has to name the suffixed form.
        */
        dispatchRef.current(
          removeNotification(
            `${PROGRESS_NOTIFICATION_ID}-${SNACKBAR_LOADING_ID_SUFFIX}`,
          ),
        );

        if (progress.outcome === 'combined') {
          // Redirected without asking: the user combined in order to go and use the account.
          window.location.assign(
            entityId
              ? `/campaign-manager/all-campaigns?entityId=${entityId}`
              : '/ads-account/combine',
          );
          return;
        }

        if (progress.outcome === 'failed') {
          dispatchRef.current(
            addNotification({
              id: 'combine-accounts-error',
              type: SnackbarTypes.Error,
              message: intlRef.current.getMessage(
                COMBINE_ACCOUNTS_CONFIRM_ERROR_KEY,
              ),
            }),
          );
          return;
        }

        /*
          Still running when the follow window closed. Nothing further is said, on purpose: the
          combine continues server-side and no failure signal exists to read, so claiming either
          outcome would assert something nothing observed. Reporting this case properly is deferred.
        */
      })
      .catch(() => {
        /*
          The watch swallows read errors itself, so reaching here means something unexpected. The
          progress snackbar is cleared rather than left up for ever with nothing behind it.
        */
        if (abandoned) {
          return;
        }
        dispatchRef.current(
          removeNotification(
            `${PROGRESS_NOTIFICATION_ID}-${SNACKBAR_LOADING_ID_SUFFIX}`,
          ),
        );
      });

    return () => {
      abandoned = true;
      controller.abort();
    };
  }, []);

  return null;
};

export default CombineProgressNotifier;
